import { Component, OnDestroy, OnInit } from '@angular/core';
import * as mapboxgl from 'mapbox-gl';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { io as socketIO } from 'socket.io-client';
import { IOgnData } from '@ogn-tracker/common/build/ogn';
import { AntennaType } from '@ogn-tracker/common/build/enum/antenna-type';
export interface SocketData {
  marker: mapboxgl.Marker;
  data: IOgnData;
  history: number[][];
  source: string;
  color: string;
}
import { layers } from './layers';
import { FollowingStore } from 'src/app/store/following/state/following.store';
import { FollowingQuery } from 'src/app/store/following/state/following.query';
import { SubSink } from 'subsink';
import { AntennasQuery } from 'src/app/store/antennas/state/antennas.query';
import { AntennasState } from 'src/app/store/antennas/state/antennas.store';
import { MapListQuery } from 'src/app/store/map-list/state/map-list.query';
import { AircraftQuery } from 'src/app/store/aircraft/state/aircraft.query';
import { Aircraft } from '@ogn-tracker/common/build/aircraft';
import { VehiclesState } from '../../store/vehicles/state/vehicles.store';
import { VehiclesQuery } from '../../store/vehicles/state/vehicles.query';
import { AircraftType } from '@ogn-tracker/common/build/enum/aircraft-type';
interface Antenna {
  data: AntennaData;
  img: mapboxgl.Marker;
}
interface AntennaData {
  type: AntennaType;
  name: string;
  lat: number;
  lon: number;
  online: boolean;
}
@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
})
export class MapComponent implements OnInit, OnDestroy {
  map: mapboxgl.Map;
  style = 'mapbox://styles/mapbox/streets-v11';
  lat = 47.1801155;
  lng = 19.5018074;

  currentOgnData: SocketData[] = [];
  bound: mapboxgl.LngLatBounds;
  io = socketIO(environment.api, { path: '/api/socket.io' });
  antennas: Antenna[] = [];
  currentAntennasOnMap: string[] = [];
  currentOgnOnMap: string[] = [];
  currentSelectedVehicle: string;
  followCurrentVehicle: boolean;
  showAntenna: AntennasState = {
    getterAntenna: null,
    selfAntenna: null,
  };
  ownedVehicles: Aircraft[] = [];
  showAircraftTypes: VehiclesState = null;

  subs = new SubSink();
  constructor(
    private http: HttpClient,
    private followingQuery: FollowingQuery,
    private antennaQuery: AntennasQuery,
    private aircrafTypeQuery: VehiclesQuery,
    private followingStore: FollowingStore,
    private mapListQuery: MapListQuery,
    private aircraftQuery: AircraftQuery
  ) {}
  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngOnInit(): void {
    this.subs.sink = this.followingQuery.select().subscribe((x) => {
      this.followCurrentVehicle = x.followCenterVehicle;
      if (!x.currentVechile) {
        this.currentSelectedVehicle = null;
      }
    });

    this.subs.sink = this.antennaQuery.select().subscribe((x) => {
      this.showAntenna = x;
      this.antennaFilter();
    });

    this.subs.sink = this.aircrafTypeQuery.select().subscribe((x) => {
      this.showAircraftTypes = x;
      this.elementFilter();
    });

    this.subs.sink = this.mapListQuery
      .select()
      .subscribe((x) => (x.showAirspace ? this.addAirspaces() : this.removeAirspaces()));

    this.subs.sink = this.aircraftQuery
      .select()
      .subscribe((x) => (this.ownedVehicles = x.aircrafts));

    this.http
      .get(environment.api + '/api/antennas')
      .subscribe((x: AntennaData[]) => this.antennaCreator(x));
    this.map = new mapboxgl.Map({
      accessToken: environment.mapbox.accessToken,
      container: 'map',
      style: this.style,
      zoom: 7,
      center: [this.lng, this.lat],
      interactive: true,
      pitchWithRotate: false,
      dragRotate: false,
    });
    // Add map controls
    this.map.addControl(new mapboxgl.NavigationControl());
    this.bound = this.map.getBounds();
    this.map.on('drag', () => {
      if (this.followCurrentVehicle) {
        this.followingStore.update((state) => ({
          ...state,
          followCenterVehicle: false,
        }));
      }
    });
    this.map.on('dragend', () => {
      this.bound = this.map.getBounds();
      this.emitFilterEvent();
      this.antennaFilter();
      this.elementFilter();
    });
    this.map.on('zoomend', () => {
      this.bound = this.map.getBounds();
      this.emitFilterEvent();
      this.antennaFilter();
      this.elementFilter();
    });
    this.io.on('connect', () => {
      this.emitFilterEvent();
    });
    this.io.on('data', (data: IOgnData) => {
      const { CN, regist, deviceId, latitude, longitude } = data;
      if (
        deviceId + regist + CN === this.currentSelectedVehicle &&
        this.followCurrentVehicle
      ) {
        this.map.jumpTo({ center: [longitude, latitude] });
      }
      if (deviceId + regist + CN === this.currentSelectedVehicle) {
        this.followingStore.update((state) => ({
          ...state,
          currentVechile: data,
        }));
      }

      if (
        this.currentOgnData.filter((x) =>
          x.data.deviceId
            ? x.data.deviceId === data.deviceId
            : x.data.regist === data.regist && x.data.CN === x.data.CN
        ).length > 0
      ) {
        this.currentOgnData.map((s) => {
          if (
            s.data.deviceId === data.deviceId &&
            s.data.regist === data.regist &&
            s.data.CN === data.CN
          ) {
            s.data = data;
            s.marker.getElement().removeEventListener('click', null);
            s.marker.remove();
            s.history.push([data.longitude, data.latitude]);
            s.marker = this.elementCreator(data, s);
          }
          return s;
        });
      } else {
        this.currentOgnData.push({
          data,
          marker: this.elementCreator(data),
          history: [[data.longitude, data.latitude]],
          source: data.deviceId + data.regist + data.CN,
          color: colors[Math.floor(Math.random() * colors.length)],
        });
      }
    });
  }

  private pathCreator(param: SocketData) {
    if (param.history.length > 2) {
      this.map.removeLayer(param.source);
      this.map.removeSource(param.source);
    }
    if (param.history.length > 1) {
      let onMapFirstIndex;
      for (let index = param.history.length - 1; index > 0; index--) {
        if (
          param.history[index][0] > this.bound.getEast() ||
          param.history[index][0] < this.bound.getWest() ||
          param.history[index][1] > this.bound.getNorth() ||
          param.history[index][1] < this.bound.getSouth()
        ) {
          onMapFirstIndex = index;
          break;
        }
      }
      this.map.addSource(param.source, {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: [...param.history.slice(onMapFirstIndex - 1, param.history.length)],
          },
        },
      });
      this.map.addLayer({
        id: param.source,
        type: 'line',
        source: param.source,
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': param.color,
          'line-width': 3,
        },
      });
    }
  }

  private emitFilterEvent() {
    this.io.emit('filter', [
      {
        lat: this.bound.getNorth(),
        lon: this.bound.getWest(),
      },
      {
        lat: this.bound.getSouth(),
        lon: this.bound.getEast(),
      },
    ]);
  }

  private elementCreator(data: IOgnData, opt?: SocketData): mapboxgl.Marker {
    const markerImage = new Image(40, 40);
    markerImage.src = assets[data.type] ?? assets[0];
    if (
      this.ownedVehicles.length > 0 &&
      this.ownedVehicles.find(
        (x) => x.deviceId === data.deviceId && x.registrationNumber === data.regist
      )
    ) {
      markerImage.style.filter =
        'invert(13%) sepia(97%) saturate(4655%) hue-rotate(294deg) brightness(96%) contrast(118%)';
    }
    if (opt?.history.length > 0) {
      this.pathCreator(opt);
    }
    const marker = new mapboxgl.Marker({
      element: markerImage,
      draggable: false,
      rotation: data.track,
      clickTolerance: 3,
    }).setLngLat([data.longitude, data.latitude]);

    //Dont like it but the documented option not works
    //Prob need refactor
    let that = this;
    marker.getElement().addEventListener('click', function (e) {
      that.currentSelectedVehicle = data.deviceId + data.regist + data.CN;
      that.followingStore.update((state) => ({
        ...state,
        currentVechile: data,
        followCenterVehicle: false,
      }));
    });
    if (this.dataShouldBeVisible(data)) {
      marker.addTo(this.map);
    }
    return marker;
  }

  private antennaCreator(antennas: AntennaData[]) {
    antennas.forEach((x) => {
      const markerImage = new Image(40, 30);
      markerImage.src = '../../../assets/ANT.png';
      markerImage.style.opacity = x.online ? '1' : '0.35';
      if (x.type === AntennaType.LATCAS) {
        markerImage.style.filter =
          'invert(25%) sepia(99%) saturate(7433%) hue-rotate(295deg) brightness(91%) contrast(114%)';
      }
      const marker = new mapboxgl.Marker({
        element: markerImage,
        draggable: false,
      }).setLngLat([x.lon, x.lat]);
      this.antennas.push({ data: x, img: marker });
      this.antennaFilter();
    });
  }

  private antennaFilter() {
    this.antennas.forEach((x) => {
      if (
        ((this.showAntenna.getterAntenna && x.data.type === AntennaType.OTHER) ||
          (this.showAntenna.selfAntenna && x.data.type === AntennaType.LATCAS)) &&
        !this.currentAntennasOnMap.includes(x.data.name) &&
        this.isInsideViewport(x.data.lat, x.data.lon)
      ) {
        x.img.addTo(this.map);
        this.currentAntennasOnMap.push(x.data.name);
      }
      if (
        (((!this.showAntenna.getterAntenna && x.data.type === AntennaType.OTHER) ||
          (!this.showAntenna.selfAntenna && x.data.type === AntennaType.LATCAS)) &&
          x) ||
        (this.currentAntennasOnMap.includes(x.data.name) &&
          !this.isInsideViewport(x.data.lat, x.data.lon))
      ) {
        x.img.remove();
        this.currentAntennasOnMap = this.currentAntennasOnMap.filter((z) => z !== x.data.name);
      }
    });
  }

  private isInsideViewport(lat: number, lon: number) {
    return (
      lon <= this.bound.getEast() &&
      lon >= this.bound.getWest() &&
      lat <= this.bound.getNorth() &&
      lat >= this.bound.getSouth()
    );
  }

  // TODO nem túl jó megnevezés
  private dataShouldBeVisible(x: IOgnData) {
    return (
      this.isInsideViewport(x.latitude, x.longitude) && this.showAircraftTypes[x.type] === true
    );
  }

  private elementFilter() {
    this.currentOgnData.forEach((x) => {
      if (!this.currentOgnOnMap.includes(x.source) && this.dataShouldBeVisible(x.data)) {
        x.marker.addTo(this.map);
        this.currentOgnOnMap.push(x.source);
      } else if (
        this.currentOgnOnMap.includes(x.source) &&
        !this.dataShouldBeVisible(x.data)
      ) {
        x.marker.remove();
        this.currentOgnOnMap = this.currentOgnOnMap.filter((z) => z !== x.source);
      }
    });
  }

  addAirspaces() {
    this.http.get('assets/airspaces.geojson').subscribe((json: any) => {
      this.map.addSource('airspaces', { type: 'geojson', data: json });
      layers.forEach((l) => {
        this.map.addLayer(l);
      });
    });
  }

  removeAirspaces() {
    if (this.map) {
      layers.forEach((l) => {
        this.map.removeLayer(l.id);
      });
      this.map.removeSource('airspaces');
    }
  }
}

const assets: { [key in AircraftType]: string } = {
  [AircraftType._RESERVED_]: '../../../assets/Unknown.png',
  [AircraftType.GLIDER]: '../../../assets/Glider.png',
  [AircraftType.TOW_PLANE]: '../../../assets/Tow.png', // TOWNPLANE (nincsen meg a kép)
  [AircraftType.HELICOPTER]: '../../../assets/Heli.png',
  [AircraftType.PARACHUTE]: '../../../assets/Parachute.png',
  [AircraftType.DROP_PLANE]: '../../../assets/DropPlane.png',
  [AircraftType.HANG_GLIDER]: '../../../assets/HangGlider.png',
  [AircraftType.PARAGLIDER]: '../../../assets/Paraglider.png',
  [AircraftType.AIRCRAFT_REC]: '../../../assets/PoweredPlane.png',
  [AircraftType.AIRCRAFT_JET]: '../../../assets/Jet.png',
  [AircraftType.UNKNOWN]: '../../../assets/UFO.png',
  [AircraftType.BALLON]: '../../../assets/Balloon.png',
  [AircraftType.AIRSHIP]: '../../../assets/Blimp.png',
  [AircraftType.UAV]: '../../../assets/Uav.png',
  [AircraftType._RESERVED2_]: '../../../assets/Unknown.png',
  [AircraftType.STATIC_OBSTACLE]: '../../../assets/Unknown.png',
};
const colors = [
  '#c71ad4',
  '#d6de57',
  '#6a33be',
  '#7f1838',
  '#846e97',
  '#6a807e',
  '#e28c34',
  '#be0a61',
  '#6bae5d',
];
